/**
 * JW Player Analytics class for tracking video events with Mixpanel
 *
 * Usage:
 * ```typescript
 * const player = jwplayer('my-video');
 * const analytics = new JWPlayerAnalytics(player);
 * ```
 */

import { isMixpanelInitialized, mixpanel } from './mixpanel';

export class JWPlayerAnalytics {
  private player: jwplayer.JWPlayer;
  private progressTracked = new Set<number>();
  private hasStarted = false;
  private cumulativeWatchTime = 0; // Total time actually watched
  private watchTime = 0; // Total time spent since start
  private lastTimePosition = 0; // Last position from time event
  private finalStatisticsSent = false; // Ensure final stats are sent only once
  private beforeUnloadHandler: () => void; // Store reference for cleanup

  // Watched spans tracking
  private watchedSpans: { start: number; end: number }[] = [];
  private currentSpanStart?: number;

  constructor(player: jwplayer.JWPlayer) {
    this.player = player;

    // Bind the beforeunload handler to maintain proper 'this' context
    this.beforeUnloadHandler = () => this.sendVideoStatistics();

    this.setupAnalytics();
    this.setupPageUnloadHandler();
  }

  /**
   * Convert watched spans to string array format for Mixpanel
   * Format: ["0-5.2", "15.1-36.7"]
   */
  private formatWatchedSpans(): string[] {
    return this.watchedSpans.map((span) => `${span.start.toFixed(1)}-${span.end.toFixed(1)}`);
  }

  /**
   * Start tracking a new watched span
   */
  private startWatchedSpan(position: number): void {
    this.currentSpanStart ??= position;
  }

  /**
   * End the current watched span and add it to the collection
   */
  private endWatchedSpan(position: number): void {
    if (this.currentSpanStart !== undefined) {
      // Only add spans that are at least 0.5 seconds long
      if (position - this.currentSpanStart >= 0.5) {
        this.watchedSpans.push({
          start: this.currentSpanStart,
          end: position,
        });
      }
      this.currentSpanStart = undefined;
    }
  }

  /**
   * Get comprehensive metadata about the current video and player state
   * Returns a flattened object suitable for Mixpanel tracking
   */
  private getAnalyticMetadata(extra: Record<string, unknown> = {}): Record<string, unknown> {
    if (!this.player) return extra;

    const playlist = this.player.getPlaylistItem();
    const visualQuality = this.player.getVisualQuality();

    // Flatten the structure for Mixpanel
    const standard = {
      // Video properties
      video_id: playlist?.mediaid,
      video_title: playlist?.title,
      video_duration: this.player.getDuration(),
      video_file: playlist?.file,

      // Playback properties
      playback_watch_time: this.watchTime,
      playback_position: this.player.getPosition(),
      playback_state: this.player.getState(),

      // Player properties
      player_version: jwplayer.version.split('+')[0],
      player_width: this.player.getWidth(),
      player_height: this.player.getHeight(),
      player_volume: this.player.getVolume(),
      player_muted: this.player.getMute(),
      player_fullscreen: this.player.getFullscreen(),

      // Quality properties
      quality_mode: visualQuality?.mode,
      quality_reason: visualQuality?.reason,
      quality_label: visualQuality?.level?.label,
      quality_width: visualQuality?.level?.width,
      quality_height: visualQuality?.level?.height,
      quality_bitrate: visualQuality?.level?.bitrate,

      // Time tracking
      cumulative_watch_time: this.cumulativeWatchTime,
    };

    return { ...standard, ...extra };
  }

  /**
   * Track event to Mixpanel with error handling
   */
  private trackEvent(
    eventName: string,
    properties: Record<string, unknown> = {},
    options?: Record<string, unknown>,
  ): void {
    if (!isMixpanelInitialized()) {
      return;
    }

    try {
      mixpanel.track(eventName, properties, options);
    } catch (error) {
      console.error('Error tracking event:', eventName, error);
    }
  }

  /**
   * Set up page unload handler to send statistics before page leave
   */
  private setupPageUnloadHandler(): void {
    if (globalThis.window) {
      globalThis.window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }
  }

  /**
   * Send video statistics event with cumulative data
   */
  private sendVideoStatistics(): void {
    if (this.hasStarted && !this.finalStatisticsSent) {
      // End current span if still active
      this.endWatchedSpan(this.player.getPosition());

      // Include watched spans only in final statistics
      const finalMetadata = {
        ...this.getAnalyticMetadata(),
        watched_spans: this.formatWatchedSpans(),
      };

      this.trackEvent('video_statistics', finalMetadata, { transport: 'sendBeacon' });
      this.finalStatisticsSent = true;
      this.cumulativeWatchTime = 0;
      this.watchedSpans = [];
    }
  }

  /**
   * Track progress milestones (10%, 25%, 50%, 75%, 90%)
   * Only triggers milestones for cumulative watch time, not current position
   */
  private trackProgress(currentPosition: number): void {
    const duration = this.player.getDuration();

    if (duration <= 0) return;

    // Calculate time difference and add to cumulative if positive
    const timeDifference = currentPosition - this.lastTimePosition;
    if (timeDifference > 0 && timeDifference < 2) {
      // Ignore large jumps (seeks)
      this.cumulativeWatchTime += timeDifference;
      this.watchTime += timeDifference;
    }

    // Store current position for next calculation
    this.lastTimePosition = currentPosition;

    // Calculate milestone percentages based on watch time
    const cumulativePercentage = Math.round((this.watchTime / duration) * 100);
    const milestones = [10, 25, 50, 75, 90];

    for (const milestone of milestones) {
      if (cumulativePercentage >= milestone && !this.progressTracked.has(milestone)) {
        this.progressTracked.add(milestone);
        this.trackEvent(`video_${milestone}_percent`, this.getAnalyticMetadata());
      }
    }
  }

  /**
   * Set up all analytics event listeners
   */
  private setupAnalytics(): void {
    if (!this.player) {
      console.warn('JW Player instance not available for analytics setup');
      return;
    }

    this.player.on('meta', () => {
      this.resetProgress();
    });

    // Track video start (first play)
    this.player.on('firstFrame', () => {
      if (!this.hasStarted) {
        this.hasStarted = true;
        this.lastTimePosition = 0;
        this.startWatchedSpan(0);
        this.trackEvent('video_start', this.getAnalyticMetadata());
      }
    });

    // Track video completion
    this.player.on('complete', () => {
      this.endWatchedSpan(this.player.getDuration());
      this.trackEvent('video_complete', this.getAnalyticMetadata());
    });

    // Track progress milestones and cumulative time during playback
    this.player.on('time', (event) => {
      if (this.hasStarted) {
        this.trackProgress(event.position);

        if (this.currentSpanStart === null) {
          this.startWatchedSpan(event.position);
        }
      }
    });

    // Track seek events
    this.player.on('seek', (event) => {
      // End current span before seek and start new one after
      this.endWatchedSpan(event.position);
      this.startWatchedSpan(event.offset);

      this.trackEvent(
        'video_seek',
        this.getAnalyticMetadata({
          seek_from: event.position,
          seek_to: event.offset,
        }),
      );

      // Reset time position to seeked position for accurate time tracking
      this.lastTimePosition = event.offset;
    });

    // Track quality changes
    this.player.on('visualQuality', () => {
      this.trackEvent('video_quality_change', this.getAnalyticMetadata());
    });

    // Track fullscreen changes
    this.player.on('fullscreen', () => {
      this.trackEvent('video_fullscreen_toggle', this.getAnalyticMetadata());
    });

    // Track volume changes
    this.player.on('volume', () => {
      this.trackEvent('video_volume_change', this.getAnalyticMetadata());
    });

    // Track mute/unmute
    this.player.on('mute', () => {
      this.trackEvent('video_mute_toggle', this.getAnalyticMetadata());
    });

    // Track errors
    this.player.on('error', (event) => {
      this.trackEvent(
        'video_error',
        this.getAnalyticMetadata({
          error_code: event.code,
          error_message: event.message,
        }),
      );
    });

    // Send final statistics when player is removed
    this.player.on('remove', () => {
      this.sendVideoStatistics();
    });
  }

  /**
   * Reset progress tracking (useful when playlist changes)
   */
  private resetProgress(): void {
    this.progressTracked.clear();
    this.hasStarted = false;
    this.lastTimePosition = 0;
    this.watchTime = 0;
    this.finalStatisticsSent = false;
    this.currentSpanStart = undefined;
  }

  /**
   * Destroy the analytics instance and clean up event listeners
   */
  public destroy(): void {
    // Remove page unload handler
    if (globalThis.window) {
      globalThis.window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }

    if (this.player) {
      // Send final statistics before cleanup
      this.sendVideoStatistics();

      // JW Player automatically cleans up event listeners when destroyed
      // but we can explicitly remove them if needed
      this.player.off('meta');
      this.player.off('firstFrame');
      this.player.off('complete');
      this.player.off('time');
      this.player.off('seek');
      this.player.off('visualQuality');
      this.player.off('fullscreen');
      this.player.off('volume');
      this.player.off('mute');
      this.player.off('error');
      this.player.off('remove');
    }
  }
}
